import React, { useState, useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "../../../helpers";
import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";
import { Toggle } from "../../../components";
import { defaultFieldTeamReportTables, mapTableNames } from "../utils";
import { Checkbox, CircularProgress } from "@mui/material";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import _ from "lodash";

// Constants
const RELOAD_DELAY_MS = 2000; // 2 seconds
const CHECKBOX_COLOR = "#6366f1";
const CHECKBOX_DISABLED_COLOR = "#d1d5db";

// Types
type UserRole = "root" | "client" | "superadmin" | "partyworker" | "backoffice" | "surveyworker" | "qaworker";

interface TableConfig {
  enabled: boolean;
  roles: UserRole[];
}

type TableSettings = Record<string, TableConfig>;

interface RoleConfig {
  value: UserRole;
  label: string;
  order: number;
}

interface TableRowProps {
  tableKey: string;
  displayName: string;
  config: TableConfig;
  roles: RoleConfig[];
  onToggleEnabled: (tableKey: string) => void;
  onToggleRole: (tableKey: string, role: UserRole) => void;
}

// Role configuration with display labels
const ROLE_CONFIG: RoleConfig[] = [
  { value: "superadmin", label: "Super Admin", order: 1 },
  { value: "client", label: "Client", order: 2 },
  { value: "partyworker", label: "Party Worker", order: 3 },
  { value: "surveyworker", label: "Survey Worker", order: 5 },
  { value: "qaworker", label: "QA Worker", order: 6 },
];

// Helper functions
const getSortedRoles = (): RoleConfig[] => {
  return [...ROLE_CONFIG].sort((a, b) => a.order - b.order);
};

const hasRoleAccess = (roles: UserRole[], role: UserRole): boolean => {
  return roles.includes(role);
};

const toggleRoleAccess = (roles: UserRole[], role: UserRole): UserRole[] => {
  return roles.includes(role)
    ? roles.filter((r) => r !== role)
    : [...roles, role];
};

const getActiveProjectFromStorage = () => {
  const projectJson = localStorage.getItem("__active_project");
  return projectJson ? JSON.parse(projectJson) : null;
};

const updateProjectInStorage = (updatedProject: any) => {
  localStorage.setItem("__active_project", JSON.stringify(updatedProject));
};

// Table Row Component
const TableRow: React.FC<TableRowProps> = ({
  tableKey,
  displayName,
  config,
  roles,
  onToggleEnabled,
  onToggleRole,
}) => {
  const isDisabled = !config.enabled;
  const rowBgClass = isDisabled ? "bg-gray-50" : "bg-white";
  const textClass = isDisabled ? "text-gray-400" : "text-gray-900";

  return (
    <tr className={`border-b border-gray-200 ${rowBgClass} hover:bg-gray-50 transition-colors`}>
      <td className="px-4 py-3">
        <div className="flex items-center justify-center">
          <Toggle
            val={config.enabled}
            onToggle={() => onToggleEnabled(tableKey)}
          />
        </div>
      </td>

      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className={`font-medium ${textClass}`}>{displayName}</span>
          {isDisabled && (
            <div className="group relative">
              <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" />
              <div className="absolute left-0 top-6 hidden group-hover:block z-10 bg-amber-50 border border-amber-200 rounded-lg p-2 shadow-lg whitespace-nowrap">
                <p className="text-xs font-medium text-amber-800">
                  Table is currently disabled
                </p>
              </div>
            </div>
          )}
        </div>
      </td>

      {roles.map((role) => {
        const hasAccess = hasRoleAccess(config.roles, role.value);

        return (
          <td key={role.value} className="p-1">
            <div className="flex items-center justify-center">
              <Checkbox
                checked={hasAccess}
                disabled={isDisabled}
                onChange={() => onToggleRole(tableKey, role.value)}
                sx={{
                  color: CHECKBOX_COLOR,
                  "&.Mui-checked": { color: CHECKBOX_COLOR },
                  "&.Mui-disabled": { color: CHECKBOX_DISABLED_COLOR },
                }}
              />
            </div>
          </td>
        );
      })}
    </tr>
  );
};

// Main Component
export const SurveyTableSettings = observer(() => {
  const { projectStore, surveyResponseStore } = useStore();

  // Get project-specific table config or use defaults
  const projectTableConfig = useMemo(() => {
    return projectStore.getProjectProperty(
      "field_team_report_tables",
      defaultFieldTeamReportTables
    );
  }, [projectStore.activeProject]);

  const [tableSettings, setTableSettings] = useState<TableSettings>(projectTableConfig);
  const [hasChanges, setHasChanges] = useState(false);

  const sortedRoles = getSortedRoles();

  useEffect(() => {
    setTableSettings(projectTableConfig);
    setHasChanges(false);
  }, [projectTableConfig]);

  const updateLocalStorageAndStore = (variables: any) => {
    const activeProject = getActiveProjectFromStorage();

    if (!activeProject?.id) {
      throw new Error("Active project not found");
    }

    const updatedProject = _.cloneDeep(activeProject);

    // Ensure properties object exists
    if (!updatedProject.properties || typeof updatedProject.properties !== "object") {
      updatedProject.properties = {};
    }

    // Merge the updated properties
    updatedProject.properties = {
      ...updatedProject.properties,
      ...variables.properties,
    };

    updateProjectInStorage(updatedProject);
    projectStore.update_active_project(updatedProject);
  };

  const { mutate: updateTableSettings, isLoading: isSaving } = useMutation({
    mutationFn: async (payload: { id: number; properties: any }) => {
      return await projectStore.editProjectProperties(payload);
    },
    onSuccess: (_, variables) => {
      try {
        updateLocalStorageAndStore(variables);
        
        toast.success("Table settings saved successfully");
        setHasChanges(false);
        surveyResponseStore.setShowTableSettingsModal(false);

        // Reload page to ensure all components reflect the changes
        setTimeout(() => {
          // window.location.reload();
        }, RELOAD_DELAY_MS);
      } catch (error) {
        console.error("Error updating project after save:", error);
        toast.error("Settings saved but failed to update local state");
      }
    },
    onError: (error) => {
      console.error("Error saving table settings:", error);
      toast.error("Failed to save table settings");
    },
  });

  const handleToggleEnabled = (tableKey: string) => {
    setTableSettings((prev) => ({
      ...prev,
      [tableKey]: {
        ...prev[tableKey],
        enabled: !prev[tableKey]?.enabled,
      },
    }));
    setHasChanges(true);
  };

  const handleToggleRole = (tableKey: string, role: UserRole) => {
    setTableSettings((prev) => {
      const currentConfig = prev[tableKey] || { enabled: true, roles: [] };
      return {
        ...prev,
        [tableKey]: {
          ...currentConfig,
          roles: toggleRoleAccess(currentConfig.roles, role),
        },
      };
    });
    setHasChanges(true);
  };

  const handleSave = () => {
    try {
      const activeProject = getActiveProjectFromStorage();

      if (!activeProject?.id) {
        toast.error("Project not found");
        return;
      }

      const updatedProject = _.cloneDeep(activeProject);

      // Ensure properties structure exists
      if (!updatedProject.properties) {
        updatedProject.properties = {};
      }

      // Get or create field_team_report_tables with proper structure
      const existingTableConfig = updatedProject.properties.field_team_report_tables;
      if (!existingTableConfig || !existingTableConfig.type) {
        updatedProject.properties.field_team_report_tables = {
          id: existingTableConfig?.id || `ftr_table_${Date.now()}`,
          type: "json",
          value: existingTableConfig?.value || {},
        };
      }

      // Update table settings value
      updatedProject.properties.field_team_report_tables.value = tableSettings;

      updateTableSettings({
        id: updatedProject.id,
        properties: updatedProject.properties,
      });
    } catch (error) {
      console.error("Error preparing table settings payload:", error);
      toast.error("Failed to prepare table settings for save");
    }
  };

  const handleReset = () => {
    setTableSettings(projectTableConfig);
    setHasChanges(false);
  };

  return (
    <div className="space-y-6 relative">
      {/* Loading Overlay */}
      {isSaving && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50 rounded-lg">
          <div className="flex flex-col items-center gap-3">
            <CircularProgress size={40} />
            <p className="text-sm font-medium text-gray-700">
              Saving table settings...
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Configure Table Visibility
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Enable or disable tables and configure role-based access
          </p>
        </div>
        {hasChanges && (
          <div className="flex gap-2">
            <button
              onClick={handleReset}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Reset to Defaults
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSaving && (
                <CircularProgress size={16} sx={{ color: "white" }} />
              )}
              {isSaving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Enabled
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[200px]">
                  Table Name
                </th>
                {sortedRoles.map((role) => (
                  <th
                    key={role.value}
                    className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider"
                  >
                    {role.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {Object.entries(mapTableNames).map(([tableKey, displayName]) => {
                const config = tableSettings[tableKey] || {
                  enabled: false,
                  roles: [],
                };

                return (
                  <TableRow
                    key={tableKey}
                    tableKey={tableKey}
                    displayName={displayName}
                    config={config}
                    roles={sortedRoles}
                    onToggleEnabled={handleToggleEnabled}
                    onToggleRole={handleToggleRole}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer Notice */}
      {hasChanges && (
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 rounded-lg shadow-lg">
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" />
            <p className="text-sm text-gray-600 font-medium">
              You have unsaved changes
            </p>
          </div>
        </div>
      )}
    </div>
  );
});
